import React, { useEffect, useState, useRef, useMemo } from 'react';
import { toolboxService } from '../../../services/api';

const Step2_Scraping = ({
  mode = 'text',
  urls,
  imageFiles = [],
  onComplete,
  onBack,
  loading,
  error
}) => {
  const [progress, setProgress] = useState({});
  const [results, setResults] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const isMountedRef = useRef(true);

  const abortControllerRef = useRef(null);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      // Cancel ongoing requests if component unmounts
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const sources = useMemo(() => {
    const urlSources = urls.map((url) => ({
      id: url,
      label: url,
      type: 'url'
    }));

    if (mode !== 'pricing') {
      return urlSources;
    }

    const fileSources = imageFiles.map((file, index) => ({
      id: `file-${index}-${file.name}`,
      label: file.name,
      type: 'file',
      file
    }));

    return [...urlSources, ...fileSources];
  }, [urls, imageFiles, mode]);

  useEffect(() => {
    if (sources.length > 0 && !isProcessing) {
      startScraping();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sources]);

  const startScraping = async () => {
    if (isProcessing) return; // Prevent multiple simultaneous runs

    setIsProcessing(true);
    const newResults = [];
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    requestAnimationFrame(() => {
      setTimeout(async () => {
        const progressUpdates = {};
        sources.forEach((source, index) => {
          progressUpdates[source.id] = {
            status: 'processing',
            index: index + 1,
            total: sources.length,
            label: source.label
          };
        });

        if (isMountedRef.current) {
          setProgress(prev => ({ ...prev, ...progressUpdates }));
        }

        const scrapingPromises = sources.map(async (source, index) => {
          if (!isMountedRef.current || signal.aborted) return null;

          try {
            let data;
            if (mode === 'pricing') {
              if (source.type === 'file' && source.file) {
                data = await toolboxService.generatePricingFromImageUpload(source.file, signal);
              } else {
                data = await toolboxService.generatePricingFromImageUrl(source.label, signal);
              }
            } else {
              data = await toolboxService.scrapeAndGenerateQA(source.label, signal);
            }

            if (!isMountedRef.current || signal.aborted) return null;

            if (data.cancelled) {
              return null;
            }

            const hasData = mode === 'pricing'
              ? (data.data?.pricing_items?.length || 0) > 0
              : Array.isArray(data.data) && data.data.length > 0;

            if (data.success && hasData) {
              setTimeout(() => {
                if (isMountedRef.current && !signal.aborted) {
                  setProgress(prev => ({
                    ...prev,
                    [source.id]: {
                      status: 'success',
                      index: index + 1,
                      total: sources.length,
                      label: source.label
                    }
                  }));
                }
              }, 0);
              return {
                url: source.type === 'url' ? source.label : null,
                fileName: source.type === 'file' ? source.label : null,
                data: data.data,
                success: true,
                sourceLabel: data.source || source.label
              };
            } else {
              const errorMsg = data.error || (mode === 'pricing'
                ? 'No pricing items or QA pairs generated from the source'
                : 'No QA pairs generated from the page');
              setTimeout(() => {
                if (isMountedRef.current && !signal.aborted) {
                  setProgress(prev => ({
                    ...prev,
                    [source.id]: {
                      status: 'error',
                      index: index + 1,
                      total: sources.length,
                      error: errorMsg,
                      label: source.label
                    }
                  }));
                }
              }, 0);
              return {
                url: source.type === 'url' ? source.label : null,
                fileName: source.type === 'file' ? source.label : null,
                data: [],
                success: false,
                error: errorMsg,
                sourceLabel: source.label
              };
            }
          } catch (err) {
            if (!isMountedRef.current || signal.aborted) return null;
            const errorMsg = err.response?.data?.detail || err.message || 'An unexpected error occurred';
            setTimeout(() => {
              if (isMountedRef.current && !signal.aborted) {
                setProgress(prev => ({
                  ...prev,
                  [source.id]: {
                    status: 'error',
                    index: index + 1,
                    total: sources.length,
                    error: errorMsg,
                    label: source.label
                  }
                }));
              }
            }, 0);
            return {
              url: source.type === 'url' ? source.label : null,
              fileName: source.type === 'file' ? source.label : null,
              data: [],
              success: false,
              error: errorMsg,
              sourceLabel: source.label
            };
          }
        });

        const promiseResults = await Promise.allSettled(scrapingPromises);

        if (!isMountedRef.current || signal.aborted) {
          setIsProcessing(false);
          return;
        }

        promiseResults.forEach((result, index) => {
          if (result.status === 'fulfilled' && result.value) {
            newResults.push(result.value);
          } else if (result.status === 'rejected') {
            const source = sources[index];
            const errorResult = {
              url: source?.type === 'url' ? source?.label : null,
              fileName: source?.type === 'file' ? source?.label : null,
              data: [],
              success: false,
              error: result.reason?.message || 'An unexpected error occurred',
              sourceLabel: source?.label
            };
            newResults.push(errorResult);
          }
        });

        if (isMountedRef.current && !signal.aborted) {
          requestAnimationFrame(() => {
            if (isMountedRef.current && !signal.aborted) {
              setResults(newResults);
              setIsProcessing(false);
              if (onComplete) {
                onComplete(newResults);
              }
            }
          });
        }
      }, 0);
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'processing':
        return (
          <svg className="animate-spin h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        );
      case 'success':
        return (
          <svg className="h-5 w-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        );
      case 'error':
        return (
          <svg className="h-5 w-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        );
      default:
        return null;
    }
  };

  const completedCount = Object.values(progress).filter(p => p.status === 'success' || p.status === 'error').length;
  const successCount = Object.values(progress).filter(p => p.status === 'success').length;
  const totalCount = sources.length;
  const summaryLabel = mode === 'pricing' ? 'source' : 'page';

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Step 2: {mode === 'pricing' ? 'Processing Pricing Lists' : 'Scraping and Generating QA'}
        </h2>
        <p className="text-gray-600">
          {mode === 'pricing'
            ? 'Extracting structured pricing data and Q&A from your images...'
            : 'Processing pages and generating Q&A pairs...'}
        </p>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-blue-900">Progress</h3>
          <span className="text-blue-700 font-medium">
            {completedCount} / {totalCount} completed
          </span>
        </div>
        <div className="w-full bg-blue-200 rounded-full h-4 mb-2">
          <div
            className="bg-blue-600 h-4 rounded-full transition-all duration-300"
            style={{ width: `${totalCount === 0 ? 0 : (completedCount / totalCount) * 100}%` }}
          ></div>
        </div>
        <div className="text-sm text-blue-700">
          {successCount} successful, {completedCount - successCount} failed
        </div>
      </div>

      <div className="bg-gray-50 p-6 rounded-lg">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Processing Status</h3>
        <div className="space-y-3">
          {sources.map((source, index) => {
            const sourceProgress = progress[source.id] || {
              status: 'pending',
              index: index + 1,
              total: totalCount,
              label: source.label
            };
            return (
              <div
                key={source.id}
                className={`flex items-center justify-between p-4 border rounded-md ${
                  sourceProgress.status === 'success' ? 'bg-green-50 border-green-200' :
                  sourceProgress.status === 'error' ? 'bg-red-50 border-red-200' :
                  sourceProgress.status === 'processing' ? 'bg-blue-50 border-blue-200' :
                  'bg-white border-gray-200'
                }`}
              >
                <div className="flex items-center flex-1 min-w-0">
                  <div className="mr-3">
                    {getStatusIcon(sourceProgress.status)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate">
                      [{sourceProgress.index}/{sourceProgress.total}] {sourceProgress.label}
                    </div>
                    {sourceProgress.error && (
                      <div className="text-xs text-red-600 mt-1 font-medium">
                        Error: {sourceProgress.error}
                      </div>
                    )}
                    {sourceProgress.status === 'success' && (
                      <div className="text-xs text-green-600 mt-1 font-medium">✓ Successfully processed</div>
                    )}
                    {sourceProgress.status === 'processing' && (
                      <div className="text-xs text-blue-600 mt-1">Processing...</div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="text-red-800 font-medium">{error}</div>
        </div>
      )}

      {completedCount === totalCount && successCount < totalCount && successCount > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="text-yellow-800">
            <strong>Warning:</strong> {totalCount - successCount} out of {totalCount} {summaryLabel}
            {totalCount !== 1 ? 's' : ''} failed to process. You can still proceed with the {successCount} successful one
            {successCount !== 1 ? 's' : ''}.
          </div>
        </div>
      )}

      {completedCount === totalCount && successCount === 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="text-red-800 font-medium">
            <strong>Error:</strong> All sources failed to process. Please check the error messages above and try again.
          </div>
        </div>
      )}

      {completedCount === totalCount && (
        <div className="flex justify-between">
          <button
            onClick={onBack}
            className="px-6 py-3 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 font-medium"
          >
            ← Back
          </button>
          <button
            onClick={() => onComplete(results)}
            disabled={successCount === 0}
            className="px-8 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            {successCount === 0
              ? 'No Data to Preview'
              : `Continue to Preview (${successCount} ${summaryLabel}${successCount !== 1 ? 's' : ''}) →`}
          </button>
        </div>
      )}
    </div>
  );
};

export default Step2_Scraping;

